import App from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import AdPixel from '@group-devs/yfp7-global-modules/components/AdPixel'
import { initialProps } from '@utils/initial'
import 'bootstrap/dist/css/bootstrap.min.css'

import '../styles/font.css'
import '../styles/button.css'
import '../styles/global.css'
import '../styles/newsletter.css'

import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import '@group-devs/yfp7-global-modules/styles/global.css'
import '@group-devs/yfp7-global-modules/styles/slides.css'
import '@group-devs/yfp7-global-modules/styles/bg.css'


/* 
  - for the sake of removing duplicate initialization/initialProps call, request for initialProps (at _app.js) is revoked for every pages (as they are already being called in each path | e.g., react\wellness-center\pages\[section]\index.js : getServerSideProps)
  - thus, leaving few routes with no source of authentication data (auth guards might misinterpret session as unauthenticated), run initialProps with these listed routes on its behalf
  TODO: remove a route from this set once its page loader supplies auth
*/
const appAuthFallbackRoutes = new Set([
  '/site/[...user]',
  '/learn-and-earn',
  '/explore/newsletter/a/RelatedContents',
  '/404',
  '/404-old',
  '/_error',
]);

class MyApp extends App {
  
  static async getInitialProps(appContext) {
    const { ctx } = appContext;
    const { req, res, query } = ctx;
    
    // Get the default app props
    const appProps = await App.getInitialProps(appContext);
    
    // Get auth data using initialProps
    let authData = {};
    if (req && appAuthFallbackRoutes.has(ctx.pathname)) {
      // Server-side
      const initialData = await initialProps(req, res, query);
      authData = initialData.auth || {};
    }
    
    return { 
      ...appProps,
      auth: authData
    };
  }
  
  // Define protected routes as a class property for consistency
  protectedRoutes = [
    '/articles',
    '/replays',
    '/file-cabinet',
    '/videos',
    '/explore/newsletter',
    '/category',
    '/events',
    '/recommended-reading',
    '/classes-and-programs',
    '/learn-and-earn',
    '/myprofile',
    '/recipes',
    '/site',
    '/welcome',
    '/home'
  ];
  
  state = {
    isCheckingAuth: true,
    isAuthenticated: false,
    isClient: false
  }

  componentDidMount() {
    // We're now on client side
    this.setState({ isClient: true });
    
    // Check authentication immediately
    this.checkAuth();
    
    // Listen for route changes
    Router.events.on('routeChangeStart', (url) => {
      this.setState({ isCheckingAuth: true });
    });
    
    Router.events.on('routeChangeComplete', (url) => {
      this.checkAuth(url);
    });

    // Your existing Facebook SDK code
    window.fbAsyncInit = function() {
      FB.init({
        appId: process.env.fbAppID,
        autoLogAppEvents : true,
        xfbml: true,
        version: 'v11.0'
      });
    };

    // Load the SDK asynchronously
    (function(d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s); js.id = id;
      js.src = "//connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  }

  componentWillUnmount() {
    // Clean up event listeners
    Router.events.off('routeChangeStart', this.checkAuth);
    Router.events.off('routeChangeComplete', this.checkAuth);
  }

  isProtectedRoute = (path) => {
    const cleanPath = path.split('?')[0].split('#')[0];
    
    return this.protectedRoutes.some(route => {
      // Check if the path exactly matches or starts with the protected route
      return cleanPath === route || cleanPath.startsWith(route + '/');
    });
  }

  checkAuth = (url) => {
    const path = url || Router.asPath;
    
    if (this.isProtectedRoute(path)) {
      const auth = this.props.pageProps?.auth || this.props.auth;
      const isAuthenticated = auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        console.log("Not authenticated, redirecting...");
        Router.push('/login');
        // Keep loading state true during redirect
      } else {
        this.setState({ isCheckingAuth: false, isAuthenticated: true });
      }
    } else {
      // Not a protected route
      this.setState({ isCheckingAuth: false });
    }
  }

  render() {
    const { Component, pageProps, auth } = this.props
    const { isCheckingAuth, isClient } = this.state
    const pixelData = pageProps.adPixel && pageProps.adPixel.pixel && pageProps.adPixel.pixel.length > 0 ? pageProps.adPixel.pixel[0] : false;

    // Check if current route is protected
    let isProtectedRoute = false;
    if (isClient) {
      isProtectedRoute = this.isProtectedRoute(Router.asPath);
    }
    
    // Merge auth into pageProps so all pages can access it
    const enhancedPageProps = {
      ...pageProps,
      auth: pageProps.auth || auth || {}
    };

    return (
      <>
        <Head>
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
          <meta name="Googlebot" content="index, follow"/>
          <meta name="BingBot" content="index, follow"/>
          <meta name="Yahoo! Slurp" content="index, follow"/>
        </Head>
        {pixelData ? <AdPixel props={pixelData}/> : ''}
        
        {/* Show loading state for protected routes while checking auth */}
        {isClient && isCheckingAuth && isProtectedRoute ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
          }}>
            <div>Loading...</div>
          </div>
        ) : (
          <Component {...enhancedPageProps} />
        )}
      </>
    )
  }
}

export default MyApp
